import { HubConnectionState } from "@microsoft/signalr";

import { globalEvents } from "@/data/Data";
import type { IAddArticleToCartCmd, IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { AppSocket } from "@/data/dao/socket/AppSocket";
import useAppSocket from "@/data/dao/socket/composables/appSocket";
import { dataEvents } from "@/data/events/dataEvents";
import { Bill, type IBill, type ISelectedItem, type IUserItemSelection } from "@/data/model/Bill";
import type { ICustomerCart, ICustomerFulfillment, ICustomerOffer } from "@/data/model/Customer";
import { DeallocationReason, FulfillmentPositionDisplayMode, ServiceType } from "@/data/model/Enums";
import type {
    BillItemSelectionChangedEvent,
    IAllocationuseCaseChangedEvent,
    ICustomerOfferNeedsRefreshEvent,
} from "@/data/model/Events";
import type { IOrderApplication } from "@/data/model/OrderApplication";
import type { ITableAllocation } from "@/data/model/Table";
import { exceptionTypes } from "@/data/util/Config";
import { Format } from "@/data/util/Format";
import { Logging } from "@/data/util/Logging";
import { Signal, TypedSignal } from "@/data/util/Signal";

import { clientLanguage } from "@/shared/localization/language";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { logInformation, logWarning } from "@/shared/logging/informations";
import { isRunnningInNativeMode, isWalleeSdkAvailable } from "@/shared/utils/androidNativeUtils";

import { Popup as ComponentPopup } from "@/components/dialog/Popup";
import { ConnectionState } from "@/components/websocket/ConnectionState";

import type { TastierClientConfig } from "@/app/TastierClient";
import { TastierClient } from "@/app/TastierClient";
import { appEvents } from "@/app/events/appEvents";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { SessionExpiredPopup } from "@/app/popups/SessionExpiredPopup";
import { handleAllocationError, hasAllocationChanged } from "@/app/services/Allocation";
import { ApplicationModeService } from "@/app/services/ApplicationModeService";
import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
import { windowHistoryService } from "@/app/services/windowHistoryService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";
import { PushNotificationsHelper } from "@/app/util/PushNotificationsHelper";

export class AppContext {
    public socket: AppSocket;

    public currentAllocation?: ITableAllocation;
    public currentBill?: IBill;

    public onItemSelectionChange: Signal;
    public onAllocationChange: Signal;
    public onGuestNumberChanged: Signal;

    private customerDao: CustomerDao;
    private connectionRetries = 0;

    public onOfferChanged: Signal;
    public onCartChanged: Signal;
    public onCartDiscountChanged: Signal;
    public onFulfillmentsChanged: Signal;
    public onBeforeItemSelectionChange: Signal;
    public onBillChange: TypedSignal<IBill | undefined>;

    public currentOffer?: ICustomerOffer;

    public fulfillmentsLoaded = false; //reset this to manualy reset fulfillment cache, for instance after a payment was placed
    public currentFulfillments: Array<ICustomerFulfillment>;

    public firebaseMessagingReady: boolean = false;
    public selectedServiceId?: string;

    private allocDebounceResolvables = Array<any>();
    private allocDebounce?: number;
    private socketAllocDebounce?: number;
    private socketAllocLastFetch: number = 0;
    private socketAllocReason?: DeallocationReason;
    private orderRefreshInterval?: number;

    private billRefreshInterval?: number;
    private userCreditCardsService: UserCreditCardsService;

    private wasWebsocketConnectionLost: boolean = false;
    private nativeAppMode: boolean = false;
    private isWalleeSdkAvailable: boolean | undefined = undefined;

    public getCurrentCart(): ICustomerCart | undefined {
        return this.currentOffer?.cart;
    }

    public getCurrentCartId(): string | undefined {
        return this.getCurrentCart()?.cartId;
    }

    public isPushNotificationsReady(useCase?: IOrderApplication): boolean {
        return this.firebaseMessagingReady && PushNotificationsHelper.isPushNotificationsEnabled(useCase);
    }

    public getActiveOrderPickUpDateTime(orderId: string): Date | undefined {
        // The customer can have active fulfillments from more than one order,
        const currentOrderFulfillments = this.currentFulfillments.filter((f) => f.orderId === orderId);
        if (currentOrderFulfillments.length > 0) {
            return currentOrderFulfillments[0].orderScheduledFulfillmentDateTime;
        } else {
            return undefined;
        }
    }

    public getOrderPlacedMessageIfNotificationEnabled(useCase: IOrderApplication): string {
        const text = Format.translate(useCase.orderPlacedTextIfNotificationsEnabled, clientLanguage(), true);
        if (text != null && text != "") {
            return text;
        } else {
            if (useCase.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Duration)
                return trans.notification.notifiedWhenOrderReadyWithDuration;
            else if (useCase.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position) {
                return trans.notification.notifiedWhenOrderReadyWithPosition;
            } else {
                return "";
            }
        }
    }

    constructor() {
        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
        this.userCreditCardsService = new UserCreditCardsService();

        this.onAllocationChange = new Signal();
        this.onGuestNumberChanged = new Signal();

        this.onOfferChanged = new Signal();
        this.onCartChanged = new Signal();
        this.onCartDiscountChanged = new Signal();
        this.onFulfillmentsChanged = new Signal();

        this.currentFulfillments = new Array<ICustomerFulfillment>();
        this.onItemSelectionChange = new Signal();
        this.onBeforeItemSelectionChange = new Signal();
        this.onBillChange = new Signal();

        this.socket = useAppSocket().appSocket;
        this.socket.onConnectionChanged.add(async (state: signalR.HubConnectionState) => {
            ConnectionState.setBySignalR(state);

            const disconnectedStates = [
                HubConnectionState.Disconnected,
                HubConnectionState.Disconnecting,
                HubConnectionState.Reconnecting,
            ];

            if (disconnectedStates.includes(state)) {
                this.wasWebsocketConnectionLost = true;
                return;
            }

            if (this.wasWebsocketConnectionLost && state == HubConnectionState.Connected) {
                this.wasWebsocketConnectionLost = false;
                await this.refreshApplicationState();
            }
        });

        this.socket.onReconnected.add(() => {
            if (this.fulfillmentsLoaded) {
                this.fulfillmentsLoaded = false;
                this.loadFulfillmentsIfNeeded();
            }
        });

        this.socket.onItemSelectionChanged = (ev: BillItemSelectionChangedEvent) => {
            Logging.LogDebug("Item selection changed for identity: " + ev.identityId);
            console.log(ev);

            const identityId = TastierClient.identityContext.getIdentityId();
            Logging.LogDebug("Current identity: " + ev.identityId);

            if (identityId != ev.identityId) {
                logError("Identity mismatch! Received: " + ev.identityId + " Current: " + identityId);
                return;
            }

            if (this.currentBill === undefined) {
                logError("Current Bill not found for identity: " + ev.identityId);
                return;
            }

            this.currentBill!.selections = ev.selections;
            appEvents.appContext.ItemSelectionChanged.dispatch(ev.selections);
            this.onItemSelectionChange.dispatch(this.currentBill!.selections);
        };

        this.socket.onAllocationChange = async (reason?: DeallocationReason) => {
            if (reason === DeallocationReason.Signout) {
                await this.debounceDispatchAllocationChange(undefined, reason);
            } else {
                // the allocation used to be fetched here, once per event: the 500ms debounce
                // further down only covers the dispatch, so a burst of events from the server
                // became a burst of requests
                this.throttleSocketAllocationRefresh(reason);
            }
        };

        dataEvents.appSocket.UserSignedOut.add(() => this.signOutIfAuthenticated());
        dataEvents.appSocket.UserSessionExpired.add(() => this.showSessionExpiredPopup(true));
        globalEvents.OnSessionExpired.add(() => {
            // a request sent while the identity is replaced (sign-out, kiosk session reset) goes out without a token
            // and comes back 401 - that is no expired session, and the replacement creates a new one anyway. The
            // popup's reload at "/" would drop the kiosk's permalink and leave it on the welcome screen
            if (TastierClient.identityContext.isSigningOut) return;

            this.showSessionExpiredPopup();
        });

        this.socket.onAllocationUseCaseChange = (ev: IAllocationuseCaseChangedEvent) => {
            if (this.currentAllocation === undefined) return;

            const existingService = this.currentAllocation.services.find((c) => c.id === ev.serviceId);
            if (existingService === undefined) return;

            existingService.useCase = ev.newUseCase;
            this.onOfferChanged.dispatch();
            appEvents.appContext.OfferChanged.dispatch();
        };

        this.socket.onEmailConfirmed = (ev) => {
            const p = TastierClient.identityContext.getProfile()!;
            p.email = ev.newEmail;
            p.hasConfirmedMail = true;

            TastierClient.identityContext.aggregateIdentity(p, true);
        };

        this.onAllocationChange.add(() => {
            this.currentOffer = undefined;
            this.temporaryChangeTranslation();
        }, this);

        this.socket.onFulfillmentChanged = () => {
            this.fulfillmentsLoaded = false;
            this.loadFulfillmentsIfNeeded();
        };

        this.socket.onLinkedFulfillmentsChanged = () => {
            this.fulfillmentsLoaded = false;
            this.loadFulfillmentsIfNeeded();
        };

        this.socket.onFulfillmentPositionsChanged = () => {
            this.fulfillmentsLoaded = false;
            this.loadFulfillmentsIfNeeded();
        };

        this.socket.onLinkedFulfillmentPositionsChanged = () => {
            this.fulfillmentsLoaded = false;
            this.loadFulfillmentsIfNeeded();
        };

        this.socket.onGuestNumberChanged = (ev) => {
            if (this.currentAllocation !== undefined && this.currentAllocation.identityId === ev.identityId) {
                this.currentAllocation.guestNumber = ev.guestNumber;
                this.onGuestNumberChanged.dispatch();
            }
        };

        this.socket.onCartChanged = (ev) => {
            if (this.currentOffer !== undefined) {
                this.currentOffer.cart = ev!.cart;
                this.onCartChanged.dispatch(ev!.isUserAction);
            }
        };

        this.socket.onCartDiscountChanged = (ev) => {
            if (this.currentOffer !== undefined) {
                this.currentOffer.cart = ev!.cart;
                this.onCartDiscountChanged.dispatch();
            }
        };

        dataEvents.appSocket.OfferChanged.add((ev: ICustomerOfferNeedsRefreshEvent) => {
            if (ev.locationId !== this.currentAllocation?.locationId && ev.roomId !== this.currentAllocation?.roomId) {
                return;
            }
            this.reloadOfferWithAttributes();
        });

        this.socket.onIdentityDiscovered = (e) => {
            const p = TastierClient.identityContext.getProfile();
            if (e!.discoveredLastName !== undefined) p!.lastName = e!.discoveredLastName;
            if (e!.discoveredFirstName !== undefined) p!.firstName = e!.discoveredFirstName;

            TastierClient.identityContext.aggregateIdentity(p!, true);
        };

        this.socket.onCreditCardAttached = (newCard) => {
            this.userCreditCardsService.addCreditCardLocal(newCard);
        };

        this.socket.onCreditCardDetached = (oldId) => {
            this.userCreditCardsService.removeCreditCardLocal(oldId);
        };
    }

    public async init(config: TastierClientConfig): Promise<void> {
        try {
            await this.socket.connect();

            this.nativeAppMode = isRunnningInNativeMode();
            this.isWalleeSdkAvailable = isWalleeSdkAvailable();

            const currentAllocation = await this.customerDao.getCurrentAllocation();
            this.setCurrentAllocation(currentAllocation);
            this.temporaryChangeTranslation();
            this.connectionRetries = 0;

            if (this.hasOrderOrOfferService()) {
                this.startOrderRefresh();
            }

            if (this.hasPayService()) {
                this.startBillRefresh();
            }
        } catch (ex) {
            logError(ex);
            this.connectionRetries++;
            if (this.connectionRetries < 4) {
                // todo: review this logic. it logs off operator!
                await TastierClient.identityContext.renewAuth();
                await this.init(config);
            } else {
                throw new Error("Failed to init bill context.");
            }
        }
    }

    public updateSelectedService(serviceId?: string) {
        this.selectedServiceId = serviceId;
        appEvents.appContext.SelectedServiceChanged.dispatch();
    }

    public async reInitAfterIdentityChange(options: { reloadAllocation: boolean }): Promise<void> {
        await this.socket.stopStart();

        if (!options.reloadAllocation) {
            await this.debounceDispatchAllocationChange(this.currentAllocation, DeallocationReason.Signout);
            return;
        }

        const lastAllocation = this.currentAllocation;
        let newAllocation: ITableAllocation | undefined;

        // reload allocation because guest number could have changed
        if (lastAllocation != null) {
            await TastierClient.context.deallocate();
            await TastierClient.context.allocate(lastAllocation.locationId, lastAllocation.tableNumber);
        } else {
            newAllocation = await this.customerDao.getCurrentAllocation();
            await this.debounceDispatchAllocationChange(newAllocation, DeallocationReason.Signout);
        }

        const hasValidGuestNumber = newAllocation?.guestNumber !== undefined && newAllocation.guestNumber !== 0;

        if (hasValidGuestNumber) {
            this.onGuestNumberChanged.dispatch();
        }
    }

    public async disconnectSocket() {
        try {
            ConnectionState.disabled = true;
            await this.socket.disconnect();
        } catch (ex) {
            logError(ex);
        }
    }

    public async reactivate() {
        await this.socket.checkConnectivity();
        await this.refreshApplicationState();
    }

    public async allocateByPermalink(code: string): Promise<void> {
        // only the kiosk keeps the code in the address bar - the app has always cleaned it away,
        // and a customer's phone has the link it came from
        const isKiosk = globalStores.kioskStore?.isKioskMode === true;
        const url = isKiosk ? windowHistoryService.kioskUrl(code) : "/";

        try {
            const tableAllocation = await this.customerDao.allocateToTableWithCode({
                code: code,
                socketConnectionId: this.socket.connectionId!,
            });

            if (!tableAllocation) {
                throw new Error("Failed to allocate to table.");
            }

            if (isKiosk) {
                ApplicationModeService.getInstance().setKioskPermalinkCode(code);
            }

            windowHistoryService.pushState({ permalinkReferrer: code }, url);

            await this.debounceDispatchAllocationChange(tableAllocation);
        } catch (ex) {
            logError(ex);
            handleAllocationError({
                e: ex as DaoError,
                resetOnFailure: true,
                onReset: () => this.setCurrentAllocation(undefined),
            });

            windowHistoryService.pushState({ permalinkReferrer: code }, url);
        }
    }

    public async allocate(
        inLocation: string,
        atTableNr: number,
        additionalInfo?: string,
        resetOnFailure: boolean = true,
        orderBillId?: string
    ): Promise<boolean> {
        try {
            const tableAllocation = await this.customerDao.allocateToTable({
                tableNumber: atTableNr,
                locationId: inLocation,
                additionalInfo: additionalInfo,
                socketConnectionId: this.socket.connectionId!,
                billId: orderBillId,
            });

            if (!tableAllocation) {
                throw new Error("Failed to allocate to table.");
            }

            await this.debounceDispatchAllocationChange(tableAllocation);
            return true;
        } catch (ex) {
            logError(ex);
            handleAllocationError({
                e: ex as DaoError,
                resetOnFailure: resetOnFailure,
                onReset: () => this.setCurrentAllocation(undefined),
            });

            return false;
        }
    }

    public async allocateByOrderId(orderId: string): Promise<void> {
        try {
            const tableAllocation = await this.customerDao.allocateToTableByOrderId({
                orderId: orderId,
                socketConnectionId: this.socket.connectionId!,
            });

            if (!tableAllocation) {
                throw new Error("Failed to allocate to table.");
            }

            await this.debounceDispatchAllocationChange(tableAllocation);
        } catch (ex) {
            logError(ex);
            handleAllocationError({
                e: ex as DaoError,
                resetOnFailure: true,
                onReset: () => this.setCurrentAllocation(undefined),
            });
        }
    }

    public async deallocate(): Promise<void> {
        if (this.currentAllocation === undefined) {
            logError("No allocation existing.");
            throw new Error("No allocation existing.");
        }

        this.stopOrderRefresh();

        try {
            await this.customerDao.deallocateFromTable();
            await this.debounceDispatchAllocationChange(undefined);
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to deallocate from table");
        }
    }

    public getNativeAppMode(): boolean {
        return this.nativeAppMode;
    }

    public getIsWalleeSdkAvailable(): boolean | undefined {
        return this.isWalleeSdkAvailable;
    }

    public async removeCartsFromLocation(): Promise<void> {
        if (this.currentAllocation === undefined) {
            logError("No allocation existing.");
            throw new Error("No allocation existing.");
        }

        this.stopOrderRefresh();

        try {
            await this.customerDao.removeCartsFromLocation();
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to remove cart");
        }
    }

    public async reloadOfferWithAttributes() {
        const orderService = Helper.getCurrentOrderOrOfferService();
        if (orderService == null) {
            return;
        }

        const oldOffer = this.currentOffer;
        const orderApplication = orderService.useCase;
        const useCartOffer = orderApplication?.usePreorder === undefined ? false : orderApplication.usePreorder; // should load offer assigned to cart if it's preorder

        try {
            appEvents.appContext.OfferLoadingStarted.dispatch();
            const offer = await this.customerDao.getOffer({
                useCaseId: orderApplication?.id,
                serviceId: orderService.id,
                useCartOffer: useCartOffer,
            });
            // the offer list is built as html by onOfferChanged, so the icons and flags it embeds cannot
            // be picked up later - the attributes have to be in the store before that dispatch
            if (offer != null) {
                await globalStores.articleAttributeStore?.load(offer.locationId, clientLanguage()).catch(logError);
            }

            this.currentOffer = offer;
            this.onOfferChanged.dispatch();

            appEvents.appContext.OfferChanged.dispatch();

            if (Helper.hasCartChanged(oldOffer!, this.currentOffer)) {
                this.onCartChanged.dispatch(false);
            }
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.userNotAtTableException) {
                this.debounceDispatchAllocationChange(undefined, DeallocationReason.AutoDeallocate);
            }
        }
    }

    public async loadFulfillmentsIfNeeded() {
        if (this.fulfillmentsLoaded) return;

        //only load fulfillments for orders with activated fulfillments
        if (!Helper.hasService(ServiceType.Order)) return;

        // no token while the identity is being replaced (kiosk session reset): the request would come back
        // 401 and raise the session expired popup. fulfillmentsLoaded stays false, so the identity changed
        // event that ends the replacement loads them
        if (TastierClient.identityContext.isSigningOut || TastierClient.identityContext.getIdentityId() === undefined)
            return;

        try {
            const fulfillments = await this.customerDao.getActiveFulfillments();
            this.currentFulfillments = fulfillments;
            this.fulfillmentsLoaded = true;
            this.onFulfillmentsChanged.dispatch();
        } catch (ex) {
            logError(ex);
            this.currentFulfillments = [];
            this.fulfillmentsLoaded = true;
            this.onFulfillmentsChanged.dispatch();
        }
    }

    public removeFulfillment(id: string) {
        if (this.fulfillmentsLoaded) return;

        const index = this.currentFulfillments.findIndex((c) => c.id === id);
        if (index === -1) return;

        this.currentFulfillments.splice(index, 1);
        this.onFulfillmentsChanged.dispatch();
    }

    public async addArticleToCart(
        articleId: string,
        offerId: string,
        quantity: number,
        options: Array<IArticleWithOptions>,
        openPrice: number | undefined,
        menuCategoryId: string
    ): Promise<any> {
        const orderService = Helper.getCurrentOrderOrOfferService();
        const data: IAddArticleToCartCmd = {
            article: {
                articleId: articleId,
                menuCategoryId: menuCategoryId,
                quantity: quantity,
                options: options,
                openPrice: openPrice,
            },
            orderServiceId: orderService!.id,
            offerId: offerId,
            socketConnectionId: this.socket.connectionId!,
        };

        if (this.cartHasVouchers()) {
            await this.showChangingCartTotalWillRemoveAppliedVouchersPopup(() => this.processAddArticleToCart(data));
        } else {
            await this.processAddArticleToCart(data);
        }
    }

    private async processAddArticleToCart(data: IAddArticleToCartCmd): Promise<any> {
        try {
            const newCart = await this.customerDao.addArticleToCart(data);
            this.currentOffer!.cart = newCart;
            this.onCartChanged.dispatch(true);
        } catch (ex) {
            this.handleError(ex, trans.cart.failedToAdd);
        }
    }

    private handleError(ex: any, defaultError: string) {
        logError(ex);
        if (ex instanceof DaoError) {
            if (ex.type === exceptionTypes.paymentInProgressException) {
                Popup.show(trans.cart.paymentBlockingCart);
            } else if (ex.type === exceptionTypes.vouchersAppliedToCartException) {
                this.showChangingCartTotalWillRemoveAppliedVouchersPopup();
            } else {
                Popup.show(defaultError);
            }
        } else {
            Popup.show(defaultError);
        }
    }

    public async removeItemFormCart(itemId: string): Promise<any> {
        if (this.cartHasVouchers()) {
            await this.showChangingCartTotalWillRemoveAppliedVouchersPopup(() =>
                this.processRemoveItemFormCart(itemId)
            );
        } else {
            await this.processRemoveItemFormCart(itemId);
        }
    }

    private async processRemoveItemFormCart(itemId: string): Promise<any> {
        const orderService = Helper.getCurrentOrderOrOfferService();
        try {
            const newCart = await this.customerDao.removeItemFromCart(itemId, {
                orderServiceId: orderService?.id!,
                socketConnectionId: this.socket.connectionId!,
            });
            this.currentOffer!.cart = newCart;
            this.onCartChanged.dispatch(true);
        } catch (ex) {
            this.handleError(ex, trans.cart.failedToRemove);
        }
    }

    public async changeCartItemQuantity(itemId: string, qty: number): Promise<any> {
        if (this.cartHasVouchers()) {
            await this.showChangingCartTotalWillRemoveAppliedVouchersPopup(() =>
                this.processChangeCartItemQuantity(itemId, qty)
            );
        } else {
            await this.processChangeCartItemQuantity(itemId, qty);
        }
    }

    private async processChangeCartItemQuantity(itemId: string, qty: number): Promise<any> {
        const orderService = Helper.getCurrentOrderOrOfferService();
        try {
            const newCart = await this.customerDao.setItemQuantityInCart(itemId, {
                quantity: qty,
                orderServiceId: orderService?.id!,
                socketConnectionId: this.socket.connectionId!,
            });
            this.currentOffer!.cart = newCart;
            this.onCartChanged.dispatch(true);
        } catch (ex) {
            this.handleError(ex, trans.cart.failedToChange);
        }
    }

    private cartHasVouchers(): boolean {
        return this.currentOffer?.cart?.hasVoucherDiscount ?? false;
    }

    public temporaryChangeTranslation() {
        if (this.currentAllocation !== undefined) trans.temporaryChange(this.currentAllocation.customTranslations);
        else trans.temporaryChange();
    }

    // caps the allocation reload driven by AllocationChange socket events at one per 500ms.
    // leading edge, so a single event still refreshes at once, plus a trailing one so a burst
    // always ends with fresh data - a plain debounce would never fire while events keep coming
    private throttleSocketAllocationRefresh(reason?: DeallocationReason) {
        const wait = 500;
        this.socketAllocReason = reason;

        if (this.socketAllocDebounce !== undefined) return;

        const elapsed = Date.now() - this.socketAllocLastFetch;
        if (elapsed >= wait) {
            this.refreshAllocationFromSocket();
            return;
        }

        this.socketAllocDebounce = window.setTimeout(() => {
            this.socketAllocDebounce = undefined;
            this.refreshAllocationFromSocket();
        }, wait - elapsed);
    }

    private async refreshAllocationFromSocket() {
        this.socketAllocLastFetch = Date.now();

        try {
            const a = await this.customerDao.getCurrentAllocation();
            await this.debounceDispatchAllocationChange(a, this.socketAllocReason);
        } catch (ex) {
            logError(ex);
        }
    }

    //debounces onAllocationChange signal for 500ms
    private debounceDispatchAllocationChange(
        newAllocation: ITableAllocation | undefined,
        reason?: DeallocationReason
    ): Promise<void> {
        return new Promise((resolve) => {
            let shouldFire = false;

            if (hasAllocationChanged(this.currentAllocation, newAllocation)) {
                shouldFire = true;
            }

            if (this.currentBill?.id != newAllocation?.orderBillId) {
                shouldFire = true;
            }

            if (!shouldFire) {
                resolve();
                return;
            }

            this.allocDebounceResolvables.push(resolve);
            if (this.allocDebounce !== undefined) {
                clearTimeout(this.allocDebounce);
                this.allocDebounce = undefined;
            }

            this.allocDebounce = window.setTimeout(() => {
                this.setCurrentAllocation(newAllocation);

                if (this.currentAllocation === undefined) {
                    this.stopOrderRefresh();
                    this.stopBillRefresh();
                } else {
                    if (this.hasOrderOrOfferService()) {
                        this.startOrderRefresh();
                    }

                    if (this.hasPayService()) {
                        this.startBillRefresh();
                    }
                }

                this.onAllocationChange.dispatch(this.currentAllocation, reason);

                for (const i in this.allocDebounceResolvables) {
                    this.allocDebounceResolvables[i]();
                }

                this.allocDebounceResolvables = [];
                this.reloadBill();
            }, 500);
        });
    }

    private startBillRefresh() {
        if (this.billRefreshInterval !== undefined) return;

        this.billRefreshInterval = window.setInterval(
            () => {
                this.reloadBill();
            },
            60 * 1000 * 10
        ); //every 10 minutess

        console.log("Start bill refresh");
    }

    private startOrderRefresh() {
        if (this.orderRefreshInterval !== undefined) return;

        this.orderRefreshInterval = window.setInterval(
            () => {
                this.reloadOfferWithAttributes();
            },
            60 * 1000 * 10
        ); //every 10 minutess
    }

    private stopBillRefresh() {
        if (this.billRefreshInterval === undefined) return;

        clearInterval(this.billRefreshInterval);
        this.billRefreshInterval = undefined;
    }

    private stopOrderRefresh() {
        if (this.orderRefreshInterval === undefined) return;

        clearInterval(this.orderRefreshInterval);
        this.orderRefreshInterval = undefined;
    }

    private hasOrderOrOfferService(): boolean {
        if (this.currentAllocation === undefined) return false;

        return (
            this.currentAllocation.services.find(
                (c) => c.serviceType === ServiceType.Order || c.serviceType === ServiceType.Offer
            ) !== undefined
        );
    }

    private hasPayService(): boolean {
        if (this.currentAllocation === undefined) return false;

        return this.currentAllocation.services.find((c) => c.serviceType === ServiceType.Pay) !== undefined;
    }

    public setCurrentBill(bill: IBill | undefined) {
        if (bill == null) {
            logInformation("Set empty bill");
        } else {
            logInformation("Set bill " + bill.id);
        }

        this.currentBill = bill;
        this.onBillChange.dispatch(this.currentBill);
        appEvents.appContext.BillChanged.dispatch({ currentBill: bill });
    }

    public async refreshApplicationState(): Promise<void> {
        const currentAllocation = await this.customerDao.getCurrentAllocation();
        if (currentAllocation == null || this.currentAllocation?.tableId !== currentAllocation.tableId) {
            await this.debounceDispatchAllocationChange(currentAllocation);
            return;
        }

        //in case websocket event were missing
        if (this.currentOffer !== undefined) {
            await this.reloadOfferWithAttributes();
            return;
        }

        if (Helper.isPayService()) {
            await this.reloadBill();
        }
    }

    public async reloadBill(): Promise<void> {
        if (this.currentAllocation === undefined) return;

        try {
            const currentOrderBillId = this.currentAllocation.orderBillId;
            console.log("Reloading the bill...");
            const currentBill = await this.customerDao.getCurrentBill({ billId: currentOrderBillId });
            this.setCurrentBill(currentBill);
        } catch (ex) {
            logError(ex);
            if (ex instanceof DaoError) {
                const e = ex as DaoError;
                if (e.type === exceptionTypes.userNotAtTableException) {
                    this.setCurrentAllocation(undefined);
                    this.currentBill = undefined;
                    this.onAllocationChange.dispatch(undefined);
                }

                if (e.type === exceptionTypes.billDoesNotExistException) {
                    this.setCurrentBill(undefined);
                }
            } else {
                throw ex;
            }
        }
    }

    public getSelectedItems(): ISelectedItem[] {
        if (this.currentBill === undefined) return [] as ISelectedItem[];

        const identityId = TastierClient.identityContext.getIdentityId();
        const mySelection = this.currentBill.selections.filter((c) => c.identityId === identityId);

        return mySelection.map((x) => {
            return {
                billItemId: x.billItemId,
                quantity: x.quantity,
            };
        });
    }

    public async selectAll(): Promise<void> {
        // also called when entering the pay service, where there may be no bill yet
        if (this.currentBill == null) {
            logWarning("No bill to select all the items. To skip...");
            return;
        }

        //optimistic adding items to bill before websocket completes

        const identityId = TastierClient.identityContext.getIdentityId();
        if (identityId == null) {
            logError(new Error("Cannot select all the bill items because the identity is missing. To skip..."));
            return;
        }

        const selections = new Array<IUserItemSelection>();
        for (const item of this.currentBill.unpaidItems) {
            selections.push({
                billItemId: item.id,
                quantity: item.unpaidQuantity,
                identityId: identityId,
            });
        }

        this.currentBill.selections = selections;

        await this.sendSelectedItemsToServer(selections);
    }

    // not for a user with a voucher applied: the server rejects any change of their selection,
    // which would prompt them to remove the voucher just for entering the pay service
    public async selectAllOnEnteringPayService(): Promise<void> {
        const identityId = TastierClient.identityContext.getIdentityId();
        const hasOwnVoucherApplied = this.currentBill?.appliedDiscounts.some(
            (x) => x.voucherId != null && x.identityId === identityId
        );
        // the server ignores an unchanged selection, but the round trip and a totals refresh would still happen
        const bill = this.currentBill;
        const isEverythingSelected =
            bill != null &&
            identityId != null &&
            bill.unpaidItems.every((x) => Bill.getSelectedItemQuantity(identityId, x, bill) === x.unpaidQuantity);
        if (hasOwnVoucherApplied || isEverythingSelected) return;

        await this.selectAll();
    }

    public async selectItemsToPay(items: Array<ISelectedItem>): Promise<void> {
        //optimistic adding items to bill before websocket completes
        const identityId = TastierClient.identityContext.getIdentityId()!;
        this.currentBill!.selections = items.map((x) => {
            return {
                billItemId: x.billItemId,
                quantity: x.quantity,
                identityId: identityId,
            };
        });

        await this.sendSelectedItemsToServer(this.currentBill!.selections);
    }

    public showInvalidSelectionPopup(refreshBillOnConfirm: boolean = true) {
        const popupSettings = {
            uniqueId: "pmtconfirm",
            message: translations.pay.failedBillInvalidSelection,
            confirmButton: translations.general.ok,
            confirmHandler: async () => {
                if (refreshBillOnConfirm) await this.reloadBill();
            },
        };

        Popup.show(
            popupSettings.message,
            popupSettings.confirmHandler,
            popupSettings.confirmButton,
            undefined,
            popupSettings.uniqueId
        );
    }

    private async sendSelectedItemsToServer(
        newSelection: IUserItemSelection[],
        reloadBillOnRemovedVouchers: boolean = false
    ) {
        try {
            this.onBeforeItemSelectionChange.dispatch(this.currentBill!.selections);

            const selectItemsToPayResult = await this.socket.selectItemsToPay({
                billId: this.currentBill!.id,
                selectedItems: newSelection,
            });
            if (selectItemsToPayResult.isSuccess) {
                // update here instead of waiting for websocket response
                if (reloadBillOnRemovedVouchers) {
                    await this.reloadBill();
                } else {
                    appEvents.appContext.ItemSelectionChanged.dispatch(this.currentBill!.selections);
                    this.onItemSelectionChange.dispatch(this.currentBill!.selections);
                }
            } else {
                await this.handleSendSelectedItemsToServerError(selectItemsToPayResult.exceptionCode, () => {
                    // Vouchers has been removed so we can try again update the selection
                    this.sendSelectedItemsToServer(newSelection, true);
                });
            }
        } catch (ex) {
            logError(ex);

            // TODO: We should show diff popup with the text: Unknown error occured...
            this.showInvalidSelectionPopup();
        }
    }

    private async handleSendSelectedItemsToServerError(
        exceptionCode: string | undefined,
        onRemovedVouchers: () => void
    ) {
        logError("An error occurred while sending the selected items " + exceptionCode);

        if (exceptionCode === exceptionTypes.vouchersAppliedToBillException) {
            await this.showChangingBIllTotalWillRemoveAppliedVouchersPopup(onRemovedVouchers);
            return;
        }
    }

    private setCurrentAllocation(tableAllocation: ITableAllocation | undefined, reason?: DeallocationReason) {
        Logging.LogDebug("Current allocation: " + (tableAllocation?.tableName ?? "NONE"));

        this.currentAllocation = tableAllocation;
        appEvents.appContext.AllocationChanged.dispatch({ currentAllocation: this.currentAllocation, reason: reason });
    }

    private showSessionExpiredPopup(isAccessRightsChange: boolean = false) {
        SessionExpiredPopup.show({
            isAccessRightsChange: isAccessRightsChange,
        });
    }

    private async signOutIfAuthenticated() {
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();

        if (isAuthenticated) {
            try {
                await TastierClient.identityContext.signOut({ trigger: "user-signed-out-push" });
                logInformation("User has been signed out");
            } catch (ex) {
                logError(ex);
            }
        } else {
            logInformation("Not signed in");
        }
    }

    private showChangingCartTotalWillRemoveAppliedVouchersPopup(
        onRemoveAllCartVouchersConfirmed?: () => Promise<void>
    ) {
        return new Promise<void>((resolve) => {
            const popup = ComponentPopup.show({
                message: translations.cart.changingTotalWillRemoveAppliedVouchersMessage,
                confirmHandler: async () => {
                    try {
                        await this.customerDao.removeAllCartVouchers();
                        if (onRemoveAllCartVouchersConfirmed) {
                            await onRemoveAllCartVouchersConfirmed();
                        }
                        resolve();
                    } catch (error) {
                        logError(error);
                        resolve();
                    }
                },
                cancelHandler: () => {
                    resolve();
                },
                confirmButton: translations.cart.removeVouchers,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: true,
                confirmBtnOnTheLeft: false,
                uniqueId: "changingTotalWillRemoveAppliedVouchersPopup",
            });

            if (!popup) {
                resolve();
            }
        });
    }

    private async showChangingBIllTotalWillRemoveAppliedVouchersPopup(onRemoveVouchers: () => void) {
        Popup.show(
            translations.cart.changingTotalWillRemoveAppliedVouchersMessage,
            () => {
                this.customerDao
                    .removeAllBillVouchers({ billId: this.currentBill!.id })
                    .then(() => {
                        onRemoveVouchers();
                    })
                    .catch((err) => {
                        logError(err);

                        //TODO:
                        Popup.show("Vouchers could not be removed");
                    });
            },
            translations.cart.removeVouchers,
            translations.general.cancel,
            "changingTotalWillRemoveAppliedBillDiscountsPopup",
            false,
            true,
            true,
            () => {
                // Restore previous selection
                this.reloadBill();
            }
        );
    }
}
